import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, ExternalLink, Flame, MapPin, MessageSquare, Radio, X } from "lucide-react";
import { api } from "@/lib/api";
const EASE = [0.16, 1, 0.3, 1];

const STATUS_STYLE = {
  responding: "border-[#FF3B30]/40 bg-[#FF3B30]/15 text-[#FF5247]",
  on_scene: "border-[#FFC107]/40 bg-[#FFC107]/10 text-[#FFC107]",
  contained: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
};

const fmt = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });

export default function IncidentDetail({ incident, cleared, onClose, user }) {
  const open = Boolean(incident || cleared);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  useEffect(() => {
    setCommentText("");
    if (!incident?.id) {
      setComments([]);
      return;
    }
    const loadComments = () =>
      api
        .get(`/incidents/${incident.id}/comments`)
        .then((r) => setComments(r.data.comments))
        .catch(() => {});
    loadComments();
    const t = setInterval(loadComments, 10000);
    return () => clearInterval(t);
  }, [incident?.id]);

  const postComment = async () => {
    if (!commentText.trim() || !incident?.id) return;
    setCommentBusy(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/comments`, {
        text: commentText.trim(),
      });
      setComments((cs) => [data, ...cs]);
      setCommentText("");
    } catch (e) {}
    setCommentBusy(false);
  };

  const removeComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((cs) => cs.filter((c) => c.id !== id));
    } catch (e) {}
  };

  const ago = (iso) => {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "JUST NOW";
    if (s < 3600) return `${Math.floor(s / 60)}M AGO`;
    if (s < 86400) return `${Math.floor(s / 3600)}H AGO`;
    return `${Math.floor(s / 86400)}D AGO`;
  };

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="incident-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
        >
          <motion.aside
            key="incident-panel"
            data-testid="incident-detail-panel"
            onClick={(e) => e.stopPropagation()}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: EASE }}
            className="feed-scroll absolute right-0 top-0 h-full w-full max-w-md overflow-y-auto border-l border-[#27272A] bg-[#0D0D0D]"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-6 py-5">
              <span className="font-jb text-[10px] tracking-[0.35em] text-zinc-500">
                INCIDENT DETAIL
              </span>
              <button
                data-testid="incident-detail-close-btn"
                onClick={onClose}
                className="grid h-9 w-9 place-items-center border border-[#27272A] text-zinc-400 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
                aria-label="Close incident detail"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {cleared && !incident ? (
              <div className="px-6 py-16 text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-[#10B981]" />
                <p className="mt-6 font-chivo text-xl font-black tracking-tight text-white">
                  Incident cleared
                </p>
                <p className="mt-3 font-jb text-[11px] leading-relaxed tracking-[0.15em] text-zinc-500">
                  APPLIANCES HAVE RETURNED TO STATION.
                  <br />
                  THIS INCIDENT IS NO LONGER ON THE PAGER FEED.
                </p>
              </div>
            ) : incident ? (
              <div className="px-6 py-8">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="border border-[#27272A] bg-black/40 px-2.5 py-1 font-jb text-[10px] tracking-[0.2em] text-zinc-400">
                    {incident.code}
                  </span>
                  <span className="border border-[#27272A] bg-black/40 px-2.5 py-1 font-jb text-[10px] tracking-[0.2em] text-zinc-400">
                    {incident.agency}
                  </span>
                  <span
                    className={`border px-2.5 py-1 font-jb text-[10px] tracking-[0.2em] ${STATUS_STYLE[incident.bucket] || STATUS_STYLE.on_scene}`}
                  >
                    {incident.status.replace("_", " ")}
                  </span>
                </div>

                <h3 className="mt-5 font-chivo text-3xl font-black tracking-tighter text-white">
                  {incident.type}
                </h3>

                <div className="mt-6 space-y-3 border border-[#27272A] bg-black/30 p-4">
                  <p className="flex items-start gap-2 text-sm text-zinc-300">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF3B30]" />
                    {[incident.address, incident.locality].filter(Boolean).join(", ") || "Location on pager message"}
                  </p>
                  {incident.lat && incident.lng ? (
                    <p className="font-jb text-[10px] tracking-[0.2em] text-zinc-600">
                      {incident.lat.toFixed(5)}, {incident.lng.toFixed(5)}
                    </p>
                  ) : null}
                </div>

                <p className="mt-8 flex items-center gap-2 font-jb text-[10px] tracking-[0.35em] text-[#FF3B30]">
                  <MessageSquare className="h-3.5 w-3.5" />
                  COMMENTS ({comments.length})
                </p>
                <div className="mt-4 space-y-2" data-testid="comments-section">
                  {comments.map((c) => (
                    <div
                      key={c.id}
                      data-testid={`comment-item-${c.id}`}
                      className="border border-[#27272A] bg-black/30 p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-jb text-[10px] font-bold tracking-[0.15em] text-[#FFC107]">
                          {c.user_name.toUpperCase()}
                        </span>
                        <span className="flex items-center gap-2 font-jb text-[9px] tracking-[0.15em] text-zinc-600">
                          {ago(c.created_at)}
                          {user && (user.id === c.user_id || user.role === "admin") && (
                            <button
                              data-testid={`comment-delete-${c.id}`}
                              onClick={() => removeComment(c.id)}
                              aria-label="Delete comment"
                              className="text-zinc-600 transition-colors hover:text-[#FF5247]"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      </div>
                      <p className="mt-1.5 font-jb text-[11px] leading-relaxed text-zinc-300">
                        {c.text}
                      </p>
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <p className="font-jb text-[10px] tracking-[0.25em] text-zinc-600">
                      NO COMMENTS YET — BE THE FIRST.
                    </p>
                  )}
                </div>
                {user ? (
                  <div className="mt-3 flex gap-2">
                    <input
                      data-testid="comment-input"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && postComment()}
                      maxLength={500}
                      placeholder="ADD A COMMENT…"
                      className="flex-1 border border-[#27272A] bg-black/40 px-4 py-2.5 font-jb text-xs tracking-[0.1em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                    />
                    <button
                      data-testid="comment-submit-btn"
                      onClick={postComment}
                      disabled={commentBusy || !commentText.trim()}
                      className="bg-[#FF3B30] px-5 font-jb text-[10px] font-bold tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#FFC107] disabled:opacity-50"
                    >
                      POST
                    </button>
                  </div>
                ) : (
                  <p className="mt-3 font-jb text-[9px] tracking-[0.25em] text-zinc-600">
                    LOG IN TO COMMENT
                  </p>
                )}

                {incident.appliances.length > 0 && (
                  <>
                    <p className="mt-10 flex items-center gap-2 font-jb text-[10px] tracking-[0.35em] text-[#FF3B30]">
                      <Radio className="h-3.5 w-3.5" />
                      RESPONDING APPLIANCES
                    </p>
                <div className="mt-4 space-y-2">
                  {incident.appliances.map((a, i) => (
                    <div
                      key={`${incident.id}-app-${i}`}
                      data-testid={`incident-appliance-${i}`}
                      className="flex items-center justify-between border border-[#27272A] bg-[#0A0A0A] px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <Flame className="h-3.5 w-3.5 text-[#FF6B00]" />
                        <span className="font-jb text-xs font-bold text-white">
                          {a.callsign}
                        </span>
                      </div>
                      <span className="font-jb text-[10px] tracking-[0.2em] text-zinc-500">
                        {a.name.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
                  </>
                )}

                {(incident.channels.length > 0 || incident.mapRef || incident.weather || incident.district || incident.vehicles) && (
                  <>
                    <p className="mt-10 flex items-center gap-2 font-jb text-[10px] tracking-[0.35em] text-[#FF3B30]">
                      <Radio className="h-3.5 w-3.5" />
                      FIREGROUND
                    </p>
                    <div className="mt-4 space-y-2 border border-[#27272A] bg-black/30 p-4 font-jb text-[11px] text-zinc-400">
                      {incident.channels.length > 0 && (
                        <p>
                          <span className="text-zinc-600">CHANNELS </span>
                          {incident.channels.join(" · ")}
                        </p>
                      )}
                      {incident.mapRef && (
                        <p>
                          <span className="text-zinc-600">MELWAY </span>
                          {incident.mapRef}
                        </p>
                      )}
                      {incident.alarmLevel && (
                        <p>
                          <span className="text-zinc-600">ALARM </span>
                          {incident.alarmLevel}
                        </p>
                      )}
                      {incident.weather && (
                        <p>
                          <span className="text-zinc-600">WX </span>
                          {incident.weather.air_temp}°C · WIND {incident.weather.wind_dir} {incident.weather.wind_spd_kmh} KM/H · RH {incident.weather.rel_hum}%
                        </p>
                      )}
                      {incident.district && (
                        <p>
                          <span className="text-zinc-600">DISTRICT </span>
                          {incident.district}
                        </p>
                      )}
                      {incident.vehicles && (
                        <p>
                          <span className="text-zinc-600">VEHICLES </span>
                          {incident.vehicles}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {incident.url && (
                  <a
                    data-testid="incident-source-link"
                    href={incident.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-2 border border-[#27272A] bg-black/30 px-4 py-3 font-jb text-[10px] tracking-[0.25em] text-[#FF5247] transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
                  >
                    OFFICIAL INCIDENT PAGE
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}

                <p className="mt-10 font-jb text-[10px] tracking-[0.35em] text-[#FF3B30]">
                  PAGER LOG
                </p>
                <div className="mt-4 space-y-0" data-testid="incident-timeline">
                  {(incident.timeline || []).map((ev, i) => (
                    <div key={`${incident.id}-tl-${i}`} className="relative flex gap-4 pb-6">
                      {i < incident.timeline.length - 1 && (
                        <span className="absolute left-[5px] top-4 h-full w-px bg-[#27272A]" />
                      )}
                      <span
                        className={`relative mt-1 h-[11px] w-[11px] shrink-0 rounded-full border-2 ${
                          i === incident.timeline.length - 1
                            ? "border-[#FF3B30] bg-[#FF3B30]/30"
                            : "border-zinc-600 bg-transparent"
                        }`}
                      />
                      <div>
                        <p className="font-jb text-[10px] tracking-[0.2em] text-zinc-600">
                          {fmt(ev.t)}
                        </p>
                        <p className="mt-1 font-jb text-[11px] leading-relaxed tracking-[0.1em] text-zinc-300">
                          {ev.label}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>


                <p className="mt-6 border-t border-[#1C1C1C] pt-6 font-jb text-[9px] leading-relaxed tracking-[0.25em] text-zinc-600">
                  FRV PAGER DATA VIA POCSAG.INFO · CFA VIA VICEMERGENCY
                  <br />
                  UNOFFICIAL — CONFIRM WARNINGS AT VICEMERGENCY.GOV.AU
                  <br />
                  <span className="text-[#FF5247]">IN AN EMERGENCY, CALL 000</span>
                </p>
              </div>
            ) : null}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
